import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { intelMacro, intelProspects, type MacroRates, type ProspectsResponse } from '@aithora/shared';
import { useConsoleRole, useSession } from '../../session/SessionContext';
import { usePageTab } from '../../shell/Shell';
import { useQuery } from '../../useQuery';
import { sx } from '../../ui/designStyles';
import { useReadOnly } from '../../ui/ReadOnly';
import { ErrorState, Skeleton } from '../../ui/states';
import {
  ALL_TABS,
  INTEL_GROUPS,
  SALES_GROUPS,
  SALES_INTEL_TABS,
  TAB_LABEL,
  TAB_TITLE,
  type IntelGroup,
  type IntelState,
  type IntelTab,
  type TabProps,
} from './common';
import { M, dealsOf, discountRateOf, mapSubjectDeal } from './model';
import { FindTab } from './FindTab';
import { RelMapTab } from './RelMapTab';
import { NorthStarTab } from './NorthStarTab';
import { AnalysisTab } from './AnalysisTab';
import { DealModelTab } from './DealModelTab';
import { ScenarioTab } from './ScenarioTab';
import { PaperTradeTab } from './PaperTradeTab';
import { PortfolioTab } from './PortfolioTab';
import { TrackingTab } from './TrackingTab';
import { BenchmarksTab } from './BenchmarksTab';
import { AtlasTab } from './AtlasTab';
import './intelligence.css';
import { BuildLabel } from '../../ui/buildLabels';
import { tierLabel } from '../residents/common';

/** Tabs that draw from the deal book (the prospects the model runs on). */
const NEEDS_BOOK: ReadonlySet<IntelTab> = new Set(['target', 'deal', 'scenario', 'simulate', 'bench', 'portfolio', 'track']);

/** The tab a section's button opens: Levers and Track live are Simulate's modes. */
const groupTab = (t: IntelTab): IntelTab => (t === 'scenario' || t === 'simulate' ? 'deal' : t);

/**
 * Atlas Intelligence, Console V4 (design: isAtlasIntel T15092; aiVals L31600). Aithora-internal:
 * Super Admin and Operator; Sales sees Market (Find and the Relationship Map) only, with no
 * Ask Atlas and no deal model (decision 4, 2026-10-01). Facility Admin cannot reach the page
 * (roles.ts), and the server refuses every facility role the catalog.
 *
 * The page is three sections, Market, Facility and Deals, each with its own tabs; the
 * Facility section carries the one deal card of the facility on the right (EBITDA, price,
 * IRR, MOIC: model.ts, every Facility tab reads the same numbers).
 */
export function AtlasIntelligence() {
  const { client } = useSession();
  const readOnly = useReadOnly();
  const [params, setParams] = useSearchParams();
  const { role } = useConsoleRole();
  const sales = role === 'SALES';
  const groups: IntelGroup[] = sales ? SALES_GROUPS : INTEL_GROUPS;
  const allowed = (t: IntelTab) => (sales ? SALES_INTEL_TABS.includes(t) : true);
  const asked = ALL_TABS.find((k) => k === params.get('tab'));
  const [tab, setTabState] = useState<IntelTab>(asked && allowed(asked) ? asked : 'find');
  const [st, setSt] = useState<IntelState>({});
  const set = useCallback((patch: IntelState) => setSt((s) => ({ ...s, ...patch })), []);
  usePageTab(TAB_LABEL[tab] ?? null);

  // The deal book is Facility and Deals: never asked for as Sales (the server refuses it).
  const book = useQuery<ProspectsResponse>((signal) => intelProspects(client, { signal }), [client], { enabled: !sales });
  // The server's live WACC (FRED); the model falls back to its named 11.2% without it.
  const macro = useQuery<MacroRates>((signal) => intelMacro(client, { signal }), [client], { enabled: !sales });
  const rate = useMemo(() => discountRateOf(macro.data), [macro.data]);
  const mapDeal = useMemo(() => mapSubjectDeal(st.aiMapSubj, rate), [st.aiMapSubj, rate]);
  const deals = useMemo(() => {
    const d = dealsOf(book.data?.prospects ?? [], rate);
    return mapDeal ? [...d, mapDeal] : d;
  }, [book.data, mapDeal, rate]);
  const sel = Math.max(0, Math.min(st.aiSel ?? 0, deals.length - 1));
  const f = deals.length ? deals[sel] : null;

  const setTab = useCallback(
    (t: IntelTab) => {
      setTabState(t);
      const next = new URLSearchParams(params);
      if (t === 'find') next.delete('tab');
      else next.set('tab', t);
      setParams(next, { replace: true });
    },
    [params, setParams],
  );
  const go = useCallback(
    (t: IntelTab, patch?: IntelState) => {
      if (!allowed(t)) return;
      if (patch) set(patch);
      setTab(t);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [set, setTab, sales],
  );

  const onAtq = tab === 'atlasq';
  const tabG = groupTab(tab);
  const gCur = groups.find((g) => g.tabs.includes(tabG)) ?? groups.find((g) => g.id === st.aiLastGroup) ?? groups[0];
  const lastIn = st.aiLastTab ?? {};
  const open = (id: IntelTab, gid: IntelGroup['id']) => {
    set({ aiLastGroup: gid, aiLastTab: { ...(st.aiLastTab ?? {}), [gid]: id }, aiPrevTab: id });
    setTab(id);
  };
  const askLabel = onAtq ? 'Back to ' + TAB_LABEL[st.aiPrevTab ?? 'relmap'] : 'Ask Atlas';
  const askStyle =
    (sales ? 'display:none;' : '') +
    'flex:0 0 auto;' +
    (sales ? '' : 'display:inline-flex;') +
    'align-items:center;gap:7px;height:32px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' +
    (onAtq ? 'color:#2F5C97;background:transparent;border:1px solid rgba(47,92,151,.4);' : 'color:#fff;background:#2F5C97;border:1px solid #2F5C97;');

  const props: TabProps = { st, set, client, book, deals, f, sel, readOnly, sales, go };
  const bookGate = NEEDS_BOOK.has(tab) && book.data === null;
  const relmap = tab === 'relmap';
  const groupIsFac = !onAtq && gCur.id === 'facility';

  return (
    <div data-screen-label="Atlas Intelligence" data-testid="intel-page" style={sx('display:flex;flex-direction:column;gap:8px;min-width:0;flex:1 1 auto;min-height:0;height:100%;overflow:hidden;')}>
      <div style={sx('flex:0 0 auto;position:relative;z-index:30;display:flex;flex-direction:column;gap:6px;border-bottom:1px solid rgba(34,39,46,.12);padding-bottom:7px;')}>
        <div role="tablist" aria-label="Atlas Intelligence sections" style={sx('display:flex;align-items:center;gap:4px;flex-wrap:wrap;row-gap:6px;')}>
          {groups.map((g) => {
            const on = !onAtq && g === gCur;
            return (
              <a
                key={g.id}
                href="#"
                role="tab"
                aria-selected={on}
                className="intel-tab"
                onClick={(e) => {
                  e.preventDefault();
                  open(lastIn[g.id] ?? g.tabs[0], g.id);
                }}
                style={sx(
                  'display:inline-flex;align-items:center;gap:7px;height:32px;padding:0 13px;border-radius:8px;font-size:16px;text-decoration:none;white-space:nowrap;' +
                    (on ? 'font-weight:700;color:#22272E;background:rgba(34,39,46,.07);' : 'font-weight:600;color:#54606C;background:transparent;'),
                )}
              >
                <span>{g.label}</span>
                <span style={sx('font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;color:' + (on ? '#2F5C97' : '#54606C') + ';')}>{String(g.tabs.length)}</span>
              </a>
            );
          })}
          <span style={sx('display:flex;align-items:center;justify-content:flex-end;gap:6px;min-width:180px;flex:1 1 0;')}>
            {groupIsFac && <FacilityBar {...props} />}
            <a
              href="#"
              className="intel-ask"
              aria-hidden={sales || undefined}
              onClick={(e) => {
                e.preventDefault();
                if (sales) return;
                if (onAtq) setTab(st.aiPrevTab ?? 'relmap');
                else {
                  set({ aiPrevTab: tab });
                  setTab('atlasq');
                }
              }}
              style={sx(askStyle)}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" />
              </svg>
              <span>{askLabel}</span>
            </a>
          </span>
        </div>
        {!onAtq && (
          <div role="tablist" aria-label={gCur.label} style={sx('display:flex;align-items:center;gap:2px;flex-wrap:wrap;row-gap:4px;')}>
            {gCur.tabs.map((id) => {
              const on = tabG === id;
              return (
                <a
                  key={id}
                  href="#"
                  role="tab"
                  aria-selected={on}
                  title={TAB_TITLE[id] ?? ''}
                  className="intel-subtab"
                  onClick={(e) => {
                    e.preventDefault();
                    open(id === 'deal' && (tab === 'scenario' || tab === 'simulate') ? tab : id, gCur.id);
                  }}
                  style={sx(
                    'display:inline-flex;align-items:center;height:28px;padding:0 11px;border-radius:7px;font-size:16px;text-decoration:none;white-space:nowrap;' +
                      (on ? 'font-weight:700;color:#2F5C97;background:rgba(79,138,214,.13);' : 'font-weight:500;color:#3C454F;background:transparent;'),
                  )}
                >
                  {TAB_LABEL[id]}
                </a>
              );
            })}
            <span style={sx('margin-left:8px;font-size:14px;color:#54606C;min-width:0;flex:1 1 160px;overflow-wrap:break-word;')}>{gCur.note}</span>
            {/* V9: a read-only seat sees everything, no action buttons, and says so. */}
            {readOnly && <span data-testid="intel-readonly" style={sx('font-size:14px;color:#54606C;flex:0 0 auto;')}>{'Read-only as ' + tierLabel(role) + '.'}</span>}
          </div>
        )}
      </div>

      {tab === 'find' && <FindTab {...props} />}
      {relmap && (
        <div style={sx('flex:1 1 auto;min-height:0;min-width:0;display:flex;flex-direction:column;')}>
          <RelMapTab {...props} />
        </div>
      )}
      {!relmap && (
        <div data-intel-pane style={sx('flex:1 1 auto;min-height:0;overflow-y:auto;overflow-x:hidden;min-width:0;display:block;padding-right:3px;')}>
          {bookGate ? (
            book.error ? (
              <ErrorState error={book.error} onRetry={book.reload} />
            ) : (
              <Skeleton rows={6} label="Loading the deal book" />
            )
          ) : (
            <>
              {tab === 'northstar' && <NorthStarTab {...props} />}
              {tab === 'portfolio' && <PortfolioTab {...props} />}
              {tab === 'track' && <TrackingTab {...props} />}
              {tab === 'deal' && <DealModelTab {...props} />}
              {tab === 'simulate' && <PaperTradeTab {...props} />}
              {tab === 'atlasq' && <AtlasTab {...props} />}
              {tab === 'scenario' && <ScenarioTab {...props} />}
              {tab === 'bench' && <BenchmarksTab {...props} />}
              {tab === 'target' && <AnalysisTab {...props} />}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The Facility section's deal card line and facility picker (V4 aiCardLine, aiFacChange):
 * one deal card per facility, every Facility tab starts from these numbers. MODELED.
 */
function FacilityBar({ st, set, deals, f, sel }: TabProps) {
  const q = String(st.aiPickQ ?? '').toLowerCase().trim();
  const rows = deals.map((x, i) => ({ x, i })).filter((r) => !q || r.x.name.toLowerCase().includes(q));
  const line = f ? 'EBITDA ' + M(f.ebitda) + ' · Price ' + M(f.entry) + ' · ' + (f.irr * 100).toFixed(1) + '% IRR · ' + f.moic.toFixed(2) + '×' : '';
  return (
    <>
      <span
        title="One deal card for this facility. Every Facility tab starts from these numbers."
        style={sx('display:inline-flex;align-items:center;gap:8px;height:30px;padding:0 10px;border-radius:8px;background:rgba(34,39,46,.05);font-size:16px;color:#3C454F;white-space:nowrap;font-variant-numeric:tabular-nums;flex:0 1 auto;min-width:0;overflow:hidden;')}
      >
        <span>{line}</span>
        <BuildLabel><span style={sx('font-size:14px;font-weight:700;letter-spacing:.08em;color:#54606C;background:rgba(34,39,46,.1);border-radius:4px;padding:1px 5px;')}>MODELED</span></BuildLabel>
      </span>
      <span style={sx('position:relative;display:inline-flex;align-items:center;gap:7px;height:30px;padding:0 6px 0 10px;border-radius:8px;border:1px solid rgba(34,39,46,.16);background:#FCFAF4;font-size:16px;flex:0 1 auto;min-width:0;max-width:440px;')}>
        <span style={sx('font-size:14px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:#54606C;flex:0 0 auto;')}>Facility</span>
        <span style={sx('font-weight:700;color:#22272E;min-width:0;overflow-wrap:break-word;')}>{f ? f.name : 'None selected'}</span>
        <a
          href="#"
          className="intel-h-change"
          aria-expanded={!!st.aiPickOpen}
          onClick={(e) => {
            e.preventDefault();
            set({ aiPickOpen: !st.aiPickOpen, aiPickQ: '' });
          }}
          style={sx('flex:0 0 auto;height:22px;display:inline-flex;align-items:center;padding:0 8px;border-radius:6px;font-size:14px;font-weight:600;color:#2F5C97;text-decoration:none;white-space:nowrap;')}
        >
          Change ▾
        </a>
        {st.aiPickOpen && (
          <div style={sx('position:absolute;right:0;top:36px;z-index:60;width:340px;max-width:80vw;background:#FCFAF4;border:1px solid rgba(34,39,46,.18);border-radius:10px;box-shadow:0 12px 32px rgba(34,39,46,.16);padding:8px;display:flex;flex-direction:column;gap:6px;')}>
            <input
              value={st.aiPickQ ?? ''}
              onChange={(e) => set({ aiPickQ: e.target.value })}
              placeholder="Search facilities"
              aria-label="Search facilities"
              style={sx('height:34px;box-sizing:border-box;padding:0 10px;border-radius:7px;border:1px solid rgba(34,39,46,.2);background:#fff;font:inherit;font-size:16px;color:#22272E;outline:none;')}
            />
            <div style={sx('max-height:280px;overflow-y:auto;display:flex;flex-direction:column;')}>
              {rows.map((r) => (
                <a
                  key={r.x.id}
                  href="#"
                  className="intel-h-pick"
                  onClick={(e) => {
                    e.preventDefault();
                    set({ aiSel: r.i, aiPickOpen: false, nsDealSrc: null, nsDealFrom: '', scOcc: undefined, scMix: undefined, scMargin: undefined, scMult: undefined });
                  }}
                  style={sx('display:flex;align-items:center;gap:10px;padding:8px 10px;border-radius:7px;font-size:16px;text-decoration:none;color:#22272E;' + (r.i === sel ? 'background:rgba(79,138,214,.13);' : ''))}
                >
                  <span style={sx('font-weight:600;min-width:0;flex:1 1 auto;overflow-wrap:break-word;')}>{r.x.name}</span>
                  <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;flex:0 0 auto;')}>{(r.x.beds ? r.x.beds + ' beds' : '') + (r.x.grade ? ' · ' + r.x.grade : '')}</span>
                </a>
              ))}
              {rows.length === 0 && <div style={sx('padding:10px;font-size:16px;color:#54606C;')}>No facility matches.</div>}
            </div>
          </div>
        )}
      </span>
    </>
  );
}
